import type {
    BlinkSearchRequest,
    Card,
    ProviderErrorInformation,
} from "@amzn/ads-blink-client";
import type { AsyncState, UseAsyncActions } from "@react-hookz/web";
import { useAsync } from "@react-hookz/web";
import { useEffect } from "react";
import { useStore } from "zustand";

import { getCustomerId } from "../../api/adsApi/adsApi.config";
import { getBlinkApiClientV2 } from "../../api/blinkApi/blinkApi";
import { authStore } from "../../store/auth";
import { FeatureFlagsStore } from "../../store/featureFlags";
import { unifiedFilterStore } from "../../store/unifiedFilters";
import {
    getCurrentProviders,
    getSingleIdentifierQueryExpression,
    getUnifiedSearchQueryExpression,
    getUnifiedSearchQueryExpressionV2,
} from "../../utils/unifiedContent.utils";

export const DEFAULT_PAGE_SIZE = 12;

export interface UnifiedContentsActionResponse {
    content: Card[];
    totalHits: number;
    currentPage: number;
    errors: ProviderErrorInformation[];
}

export const emptyResponse: UnifiedContentsActionResponse = {
    content: [],
    totalHits: 0,
    currentPage: 1,
    errors: [],
};

export interface UnifiedContentsActionProps {
    advertiserId?: string;
    marketplaceId?: string;
}

export function useGetUnifiedContentsAsync({
    advertiserId,
    marketplaceId,
}: UnifiedContentsActionProps): [
    AsyncState<UnifiedContentsActionResponse>,
    UseAsyncActions<UnifiedContentsActionResponse, [number, string?]>,
] {
    const sortAttribute = useStore(
        unifiedFilterStore,
        (state) => state.sortAttribute,
    );

    const [contentsState, contentsAction] = useAsync(
        async (page: number, identifier?: string) => {
            const useUnifiedSearch = FeatureFlagsStore.getState().unifiedSearch;
            const queryExpression = identifier
                ? getSingleIdentifierQueryExpression(identifier)
                : useUnifiedSearch
                  ? getUnifiedSearchQueryExpressionV2()
                  : getUnifiedSearchQueryExpression();
            const request: BlinkSearchRequest = {
                advertiserId,
                marketplaceId,
                clientId: window.location.host,
                providerPageSize: DEFAULT_PAGE_SIZE,
                queryExpression,
                sortBy: sortAttribute,
            };

            if (useUnifiedSearch) {
                request.userAlias = authStore.getState().username;
                request.customerId = getCustomerId();
            }

            const response = await (
                await getBlinkApiClientV2()
            ).cerebroSearch({
                ...request,
                page,
                targetProviders: getCurrentProviders(),
            });

            return {
                ...emptyResponse,
                ...response,
            } as UnifiedContentsActionResponse;
        },
        emptyResponse,
    );

    useEffect(() => {
        if (contentsState.status === "success") {
            void contentsAction.execute(1);
        }
    }, [sortAttribute]);

    return [contentsState, contentsAction];
}
